import { ClipboardModule } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOption } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import {
  InformationBoxComponent,
  PartnerService,
} from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  BreadcrumbsStore,
  ButtonsContainerComponent,
  canSave$,
  DialogPresets,
  ImageUploadComponent,
  ProfileImageService,
  TypedFormControl,
  TypedFormGroup,
  UserIconComponent,
} from '@principle-theorem/ng-shared';
import { PartnerApp } from '@principle-theorem/principle-core';
import {
  ApiScope,
  IPartnerApp,
  IPartnerAppOAuth,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  findProp,
  Firestore,
  getEnumValues,
  snapshot,
  snapshotCombineLatest,
  uid,
  WithRef,
} from '@principle-theorem/shared';
import { Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { CopyClientSecretDialogComponent } from '../copy-client-secret-dialog/copy-client-secret-dialog.component';

@Component({
  selector: 'pr-app-settings',
  imports: [
    CommonModule,
    UserIconComponent,
    MatMenuModule,
    MatIcon,
    MatInput,
    MatSelect,
    MatOption,
    MatButton,
    ImageUploadComponent,
    MatCardModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    ButtonsContainerComponent,
    MatTooltip,
    ClipboardModule,
    InformationBoxComponent,
  ],
  templateUrl: './app-settings.component.html',
  styleUrl: './app-settings.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppSettingsComponent {
  app$: Observable<WithRef<IPartnerApp>>;
  storagePath$: Observable<string>;
  appIconUrl$: Observable<string | undefined>;
  apiScopes = getEnumValues(ApiScope).sort();
  form = new TypedFormGroup<IPartnerAppOAuth>({
    clientId: new TypedFormControl(''),
    clientSecrets: new TypedFormControl([]),
    redirectUri: new TypedFormControl(''),
    scopes: new TypedFormControl([]),
  });
  canSave$ = canSave$(this.form);
  apiDocsUrl = environment.apiDocsUrl;
  appUrl = environment.appUrl;
  consentUrl: Signal<string | undefined>;

  constructor(
    public snackBar: MatSnackBar,
    private _basicDialog: BasicDialogService,
    private _route: ActivatedRoute,
    partnerService: PartnerService,
    breadcrumbs: BreadcrumbsStore,
    profileImage: ProfileImageService
  ) {
    this.app$ = this._route.data.pipe(
      findProp<WithRef<IPartnerApp>>('partnerApp'),
      filterUndefined(),
      switchMap((data) => Firestore.doc$(data.ref))
    );

    this.app$
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((app) => this.form.patchValue(app.oauth));

    snapshotCombineLatest([
      partnerService.partner$.pipe(filterUndefined()),
      this.app$,
    ])
      .pipe(take(1), takeUntilDestroyed())
      .subscribe(([partner, app]) => {
        breadcrumbs.setBreadcrumbs([
          {
            label: partner.name,
            path: ['/'],
          },
          {
            label: 'Apps',
          },
          {
            label: app.name,
            path: ['/apps', app.ref.id],
          },
          { label: 'Settings' },
        ]);
      });

    this.storagePath$ = this.app$.pipe(
      map((app) => PartnerApp.storagePath(app))
    );

    this.appIconUrl$ = this.app$.pipe(
      switchMap((app) => {
        if (!app?.appIconUrl) {
          return of(undefined);
        }
        return profileImage.getUserProfileImage$({
          name: '',
          profileImageURL: app.appIconUrl,
        });
      })
    );

    const form = toSignal(this.form.valueChanges);
    this.consentUrl = computed(() => this.getConsentUrl(form()));
  }

  async updateImageUrl(storagePath: string): Promise<void> {
    const app = await snapshot(this.app$);
    await Firestore.patchDoc(app.ref, { appIconUrl: storagePath });
  }

  async regenerateSecret(): Promise<void> {
    const app = await snapshot(this.app$);
    const clientSecret = uid(true);
    const clientSecrets = [...(app.oauth?.clientSecrets ?? [])];
    clientSecrets.push(clientSecret);
    await Firestore.patchDoc(app.ref, {
      oauth: {
        ...app.oauth,
        clientSecrets,
      },
    });

    this._basicDialog.open<CopyClientSecretDialogComponent, string>(
      CopyClientSecretDialogComponent,
      DialogPresets.medium({
        data: clientSecret,
      })
    );
  }

  async submit(): Promise<void> {
    const app = await snapshot(this.app$);
    const data = this.form.getRawValue();

    await Firestore.patchDoc(app.ref, {
      oauth: {
        ...app.oauth,
        redirectUri: data.redirectUri,
        scopes: data.scopes,
      },
    });

    this.snackBar.open('App settings updated');
  }

  getConsentUrl(data?: IPartnerAppOAuth): string | undefined {
    if (!data) {
      return;
    }

    const url = new URL(`${this.appUrl}/oauth/authorise`);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('client_id', data.clientId);
    url.searchParams.set('redirect_uri', data.redirectUri);
    url.searchParams.set('scope', data.scopes.join(' '));
    url.searchParams.set('state', 'xxxxxx-xxxxxx-xxxxxx-xxxxxx');
    return url.toString();
  }
}
